import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { SUPPORTED_LANGUAGES } from "@/i18n/languages";
import { loadLanguage } from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { hapticTap } from "@/lib/native-haptics";

// Map language codes to ISO2 country codes for FlagCDN (flag-as-img - emoji unreliable on iOS WKWebView)
const LANG_ISO: Record<string, string> = {
  en: "gb", fr: "fr", sw: "ke", am: "et", ar: "sa", so: "so", zh: "cn", din: "ss",
};
function LangFlag({ lang, className }: { lang: { code: string; flag: string }; className?: string }) {
  const iso = LANG_ISO[lang.code];
  if (!iso) return <span className={className} aria-hidden>{lang.flag}</span>;
  return (
    <img
      src={`https://flagcdn.com/${iso}.svg`}
      alt=""
      aria-hidden
      loading="lazy"
      className={className}
      style={{ width: "1.25em", height: "0.9em", objectFit: "cover", borderRadius: 2 }}
    />
  );
}

export function DialerLanguageToggle() {
  const [open, setOpen] = useState(false);
  const { i18n, t } = useTranslation("portal");
  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll list to top whenever sheet opens (use rAF to wait for animation paint)
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = 0;
        });
      });
    }
  }, [open]);

  const handleSelect = useCallback(async (langCode: string) => {
    hapticTap();
    await loadLanguage(langCode);
    localStorage.setItem("elefa_language", langCode);
    setOpen(false);

    // Sync to backend profile (fire-and-forget)
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id) {
      supabase
        .from("user_profiles_extended")
        .update({ preferred_language: langCode } as Record<string, unknown>)
        .eq("user_id", session.user.id)
        .then(() => {});
    }
  }, []);

  return (
    <>
      {/* Trigger button - flag + code, consistent across all viewports */}
      <button
        onClick={() => { hapticTap(); setOpen(true); }}
        className={cn(
          "inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-full",
          "bg-muted/60 backdrop-blur-sm hover:bg-muted transition-colors",
          "active:scale-95 select-none whitespace-nowrap flex-shrink-0"
        )}
        aria-label="Change language"
      >
        <LangFlag lang={currentLang} className="flex-shrink-0" />
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide leading-none flex-shrink-0">
          {currentLang.code === 'din' ? 'DIN' : currentLang.code.toUpperCase()}
        </span>
      </button>

      {/* Portal to body so the overlay escapes #root scroll container */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/40"
              onClick={() => setOpen(false)}
            >
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", stiffness: 400, damping: 34 }}
                onAnimationComplete={() => {
                  if (listRef.current) listRef.current.scrollTop = 0;
                }}
                className={cn(
                  "bg-card w-full max-w-sm overflow-hidden shadow-2xl",
                  "rounded-t-3xl sm:rounded-2xl"
                )}
                style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 16px)" }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Drag handle - mobile affordance */}
                <div className="flex justify-center pt-3 pb-1 sm:hidden">
                  <div className="w-10 h-1 rounded-full bg-muted-foreground/20" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-5 pt-2 pb-2 sm:pt-4">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-base font-semibold text-foreground">{t("language")}</h3>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>

                {/* Language list */}
                <div ref={listRef} className="px-2 pb-8 overflow-y-auto overscroll-contain -webkit-overflow-scrolling-touch" style={{ maxHeight: "calc(100dvh - 140px)" }}>
                  {SUPPORTED_LANGUAGES.map((lang) => {
                    const isActive = i18n.language === lang.code;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => handleSelect(lang.code)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-colors min-h-[52px]",
                          isActive
                            ? "bg-primary/10"
                            : "hover:bg-muted/50 active:bg-muted"
                        )}
                      >
                        <LangFlag lang={lang} className="flex-shrink-0" />
                        <div className="flex-1 text-left min-w-0">
                          <span className={cn(
                            "text-[15px] font-medium block truncate",
                            isActive ? "text-primary" : "text-foreground"
                          )}>
                            {lang.nativeName}
                          </span>
                          {lang.nativeName !== lang.name && (
                            <span className="text-xs text-muted-foreground block truncate">
                              {lang.name}
                            </span>
                          )}
                        </div>
                        {isActive && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
